import {
  PUZZLE_BUBBLE_RADIUS,
  PUZZLE_EMPTY_INPUT,
  PUZZLE_MAX_AIM,
  PUZZLE_MIN_AIM,
  PUZZLE_ROUND_TICKS,
  PUZZLE_SHOOTER_X,
  PUZZLE_SHOOTER_Y,
  getPuzzleLegalSnapCells,
  getPuzzleNeighborPositions,
  puzzleCellKey,
  puzzleCellToPoint,
} from "../simulation";
import type {
  PuzzleBoardState,
  PuzzleBotDifficulty,
  PuzzleBubbleKind,
  PuzzleCell,
  PuzzleColor,
  PuzzleInput,
  PuzzleLaunchBubble,
  PuzzleMatchState,
} from "../simulation";

export type PuzzleBotTargetPolicy =
  | "casual"
  | "matching"
  | "tactical"
  | "optimal"
  | "omniscient";

export interface PuzzleBotProfile {
  difficulty: PuzzleBotDifficulty;
  /** Minimum idle ticks after the previous shot resolves. */
  thinkTicks: number;
  /** Deterministic extra idle ticks, sampled once for each upcoming shot. */
  thinkJitterTicks: number;
  /** Chance that this shot deliberately uses a sub-optimal plan. */
  mistakePercent: number;
  /** Maximum angular offset applied only to a mistaken shot. */
  mistakeAimErrorDegrees: number;
  targetPolicy: PuzzleBotTargetPolicy;
  /** A mistake reproducibly chooses among this many lower-ranked candidates. */
  mistakeCandidatePoolSize: number;
  holdLookahead: boolean;
  holdImprovementThreshold: number;
}

export const PUZZLE_BOT_PROFILES: Readonly<
  Record<PuzzleBotDifficulty, Readonly<PuzzleBotProfile>>
> = Object.freeze({
  easy: Object.freeze({
    difficulty: "easy",
    thinkTicks: 60,
    thinkJitterTicks: 15,
    mistakePercent: 45,
    mistakeAimErrorDegrees: 9,
    targetPolicy: "optimal",
    mistakeCandidatePoolSize: 8,
    holdLookahead: false,
    holdImprovementThreshold: Number.POSITIVE_INFINITY,
  }),
  medium: Object.freeze({
    difficulty: "medium",
    thinkTicks: 60,
    thinkJitterTicks: 15,
    mistakePercent: 25,
    mistakeAimErrorDegrees: 9,
    targetPolicy: "optimal",
    mistakeCandidatePoolSize: 8,
    holdLookahead: false,
    holdImprovementThreshold: Number.POSITIVE_INFINITY,
  }),
  hard: Object.freeze({
    difficulty: "hard",
    thinkTicks: 60,
    thinkJitterTicks: 15,
    mistakePercent: 10,
    mistakeAimErrorDegrees: 9,
    targetPolicy: "optimal",
    mistakeCandidatePoolSize: 8,
    holdLookahead: false,
    holdImprovementThreshold: Number.POSITIVE_INFINITY,
  }),
  superhuman: Object.freeze({
    difficulty: "superhuman",
    thinkTicks: 60,
    thinkJitterTicks: 15,
    mistakePercent: 3,
    mistakeAimErrorDegrees: 9,
    targetPolicy: "optimal",
    mistakeCandidatePoolSize: 8,
    holdLookahead: true,
    holdImprovementThreshold: 15_000,
  }),
  god: Object.freeze({
    difficulty: "god",
    thinkTicks: 60,
    thinkJitterTicks: 15,
    mistakePercent: 0,
    mistakeAimErrorDegrees: 9,
    targetPolicy: "omniscient",
    mistakeCandidatePoolSize: 8,
    holdLookahead: true,
    holdImprovementThreshold: 1,
  }),
});

interface AimCandidate {
  aim: number;
  score: number;
  row: number;
  col: number;
}

function clampAim(value: number): number {
  return Math.max(PUZZLE_MIN_AIM, Math.min(PUZZLE_MAX_AIM, value));
}

function stableHash(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function deterministicUnit(
  state: PuzzleMatchState,
  board: PuzzleBoardState,
  salt: string,
): number {
  const hash = stableHash(
    `${state.seed}:${state.round}:${board.playerId}:${board.shotsFired}:` +
      `${board.shotsResolved}:${board.nextBubbleOrdinal}:${salt}`,
  );
  return hash / 4_294_967_296;
}

function matchingNeighborClusterSize(
  board: PuzzleBoardState,
  row: number,
  col: number,
  color: PuzzleColor,
): number {
  const byKey = new Map(board.cells.map((cell) => [puzzleCellKey(cell), cell]));
  const visited = new Set<string>();
  const queue: PuzzleCell[] = [];
  for (const neighbor of getPuzzleNeighborPositions(row, col)) {
    const cell = byKey.get(puzzleCellKey(neighbor));
    if (cell?.color === color) {
      queue.push(cell);
    }
  }

  while (queue.length > 0) {
    const cell = queue.shift();
    if (cell === undefined) {
      continue;
    }
    const key = puzzleCellKey(cell);
    if (visited.has(key) || cell.color !== color) {
      continue;
    }
    visited.add(key);
    for (const neighbor of getPuzzleNeighborPositions(cell.row, cell.col)) {
      const candidate = byKey.get(puzzleCellKey(neighbor));
      if (candidate?.color === color && !visited.has(puzzleCellKey(candidate))) {
        queue.push(candidate);
      }
    }
  }
  return visited.size;
}

function specialCoverage(
  board: PuzzleBoardState,
  candidate: PuzzleCell,
  color: PuzzleColor,
  kind: PuzzleBubbleKind,
): number {
  if (kind === "color_burst") {
    return board.cells.filter((cell) => cell.color === color).length;
  }
  if (kind === "row_burst") {
    return board.cells.filter((cell) => cell.row === candidate.row).length;
  }
  if (kind === "column_burst") {
    const point = puzzleCellToPoint(candidate);
    return new Set(
      board.cells
        .filter(
          (cell) =>
            Math.abs(puzzleCellToPoint(cell).x - point.x) <=
            PUZZLE_BUBBLE_RADIUS + 0.001,
        )
        .map((cell) => cell.row),
    ).size;
  }
  return matchingNeighborClusterSize(
    board,
    candidate.row,
    candidate.col,
    color,
  );
}

function candidateScore(
  board: PuzzleBoardState,
  candidate: PuzzleCell,
  bubble: PuzzleLaunchBubble,
  policy: PuzzleBotTargetPolicy,
): number {
  const point = puzzleCellToPoint(candidate);
  const matchingNeighbors = matchingNeighborClusterSize(
    board,
    candidate.row,
    candidate.col,
    bubble.color,
  );
  const completesMatch = matchingNeighbors >= 2 ? 1 : 0;
  const coverage = specialCoverage(
    board,
    candidate,
    bubble.color,
    bubble.kind,
  );
  const stablePosition =
    -candidate.row * 100 -
    Math.abs(point.x - PUZZLE_SHOOTER_X) -
    candidate.col * 0.001;

  if (policy === "casual") {
    return stablePosition;
  }
  if (policy === "matching") {
    return completesMatch * 35_000 + matchingNeighbors * 5_000 + stablePosition;
  }
  if (policy === "tactical") {
    return (
      completesMatch * (bubble.kind === "normal" ? 70_000 : 0) +
      coverage * 7_500 +
      stablePosition
    );
  }

  const base =
    completesMatch * (bubble.kind === "normal" ? 100_000 : 0) +
    coverage * 10_000 +
    stablePosition;
  if (policy === "omniscient") {
    // The danger margin is fully visible authoritative information. God mode
    // gives extra weight to resolving the lowest exposed parts of the board.
    const bottomRelief = candidate.row * Math.max(1, coverage) * 25;
    return base + bottomRelief;
  }
  return base;
}

function rankedAimCandidates(
  board: PuzzleBoardState,
  bubble: PuzzleLaunchBubble,
  policy: PuzzleBotTargetPolicy,
): AimCandidate[] {
  const ranked: AimCandidate[] = [];
  for (const candidate of getPuzzleLegalSnapCells(board.cells)) {
    const point = puzzleCellToPoint(candidate);
    const verticalDistance = Math.max(1, PUZZLE_SHOOTER_Y - point.y);
    const rawAim =
      (Math.atan2(point.x - PUZZLE_SHOOTER_X, verticalDistance) * 180) /
      Math.PI;
    if (rawAim < PUZZLE_MIN_AIM || rawAim > PUZZLE_MAX_AIM) {
      continue;
    }
    ranked.push({
      aim: rawAim,
      score: candidateScore(
        board,
        { row: candidate.row, col: candidate.col, color: bubble.color },
        bubble,
        policy,
      ),
      row: candidate.row,
      col: candidate.col,
    });
  }
  ranked.sort(
    (left, right) =>
      right.score - left.score ||
      left.row - right.row ||
      left.col - right.col,
  );
  return ranked;
}

function selectAimCandidate(
  state: PuzzleMatchState,
  board: PuzzleBoardState,
  bubble: PuzzleLaunchBubble,
  profile: Readonly<PuzzleBotProfile>,
  salt: string,
  mistaken = false,
): AimCandidate {
  const ranked = rankedAimCandidates(board, bubble, profile.targetPolicy);
  if (ranked.length === 0) {
    return { aim: board.aim, score: Number.NEGATIVE_INFINITY, row: 0, col: 0 };
  }
  if (!mistaken || ranked.length === 1) return ranked[0]!;
  const poolSize = Math.min(profile.mistakeCandidatePoolSize, ranked.length - 1);
  const selectedIndex = 1 + Math.floor(
    deterministicUnit(state, board, `${salt}:mistake-target`) * poolSize,
  );
  return ranked[selectedIndex] ?? ranked[0]!;
}

function difficultyFor(
  state: PuzzleMatchState,
  playerId: string,
  override: PuzzleBotDifficulty | undefined,
): PuzzleBotDifficulty {
  if (override !== undefined) {
    return override;
  }
  const configured = state.players.find((player) => player.id === playerId)
    ?.botDifficulty;
  return configured ?? "medium";
}

/**
 * A shot's mistake decision is fixed by match/round/player/shot identity. The
 * same roll is shared by every difficulty, making lower-difficulty mistakes a
 * strict superset of higher-difficulty mistakes.
 */
export function puzzleBotWillMakeMistake(
  state: PuzzleMatchState,
  playerId: string,
  difficultyOverride?: PuzzleBotDifficulty,
): boolean {
  const board = state.boards[playerId];
  if (board === undefined) return false;
  const difficulty = difficultyFor(state, playerId, difficultyOverride);
  return deterministicUnit(state, board, "mistake-roll") * 100
    < PUZZLE_BOT_PROFILES[difficulty].mistakePercent;
}

/** Exact deterministic rest time for the bot's upcoming decision. */
export function puzzleBotDecisionDelayTicks(
  state: PuzzleMatchState,
  playerId: string,
  difficultyOverride?: PuzzleBotDifficulty,
): number {
  const board = state.boards[playerId];
  const difficulty = difficultyFor(state, playerId, difficultyOverride);
  const profile = PUZZLE_BOT_PROFILES[difficulty];
  if (board === undefined) return profile.thinkTicks;
  return profile.thinkTicks + Math.floor(
    deterministicUnit(state, board, "think-jitter") * (profile.thinkJitterTicks + 1),
  );
}

function shouldHold(
  state: PuzzleMatchState,
  board: PuzzleBoardState,
  profile: Readonly<PuzzleBotProfile>,
  current: AimCandidate,
): boolean {
  if (!profile.holdLookahead || board.holdLocked) {
    return false;
  }
  const alternate =
    board.heldBubble ??
    ({ color: board.nextColor, kind: board.nextKind } satisfies PuzzleLaunchBubble);
  const alternatePlan = selectAimCandidate(
    state,
    board,
    alternate,
    profile,
    "hold",
  );
  return alternatePlan.score > current.score + profile.holdImprovementThreshold;
}

/**
 * Produces only aim/fire/hold authority available to a human. The function is
 * a pure deterministic read: mistake probability, think delay, target
 * selection, and hold lookahead are reproducible from the serialized snapshot.
 */
export function createPuzzleBotInput(
  state: PuzzleMatchState,
  playerId: string,
  difficultyOverride?: PuzzleBotDifficulty,
): PuzzleInput {
  const board = state.boards[playerId];
  if (
    state.phase !== "playing" ||
    board === undefined ||
    board.status !== "active"
  ) {
    return { ...PUZZLE_EMPTY_INPUT };
  }
  if (board.activeShot !== null) {
    return { aim: board.aim, fire: false, hold: false };
  }

  const difficulty = difficultyFor(state, playerId, difficultyOverride);
  const profile = PUZZLE_BOT_PROFILES[difficulty];
  const decisionDelay = puzzleBotDecisionDelayTicks(state, playerId, difficulty);
  const idleTicks = board.lastShotResolvedTick >= 0
    ? state.tick - board.lastShotResolvedTick
    : PUZZLE_ROUND_TICKS - state.roundTicksRemaining;
  if (idleTicks < decisionDelay) {
    return { aim: board.aim, fire: false, hold: false };
  }

  const mistaken = puzzleBotWillMakeMistake(state, playerId, difficulty);
  const currentBubble: PuzzleLaunchBubble = {
    color: board.currentColor,
    kind: board.currentKind,
  };
  const plan = selectAimCandidate(
    state,
    board,
    currentBubble,
    profile,
    "current",
    mistaken,
  );
  if (!mistaken && shouldHold(state, board, profile, plan)) {
    return { aim: board.aim, fire: false, hold: true };
  }

  const errorUnit = deterministicUnit(state, board, "mistake-aim") * 2 - 1;
  const aim = clampAim(
    plan.aim + (mistaken ? errorUnit * profile.mistakeAimErrorDegrees : 0),
  );
  return { aim, fire: true, hold: false };
}
